import { Draft } from '@reduxjs/toolkit';
import BigNumber from 'bignumber.js';
import {
  isValidDecimal,
  parseFormattedFloat,
} from '@paradigm/utils/formatDecimal';
import { VIEW_RATIO_PRECISION } from '@paradigm/features/src/unified-rfqs/ui/ratio-precision';
import { getProduct } from '#/products/domain';
import { TradeLeg, TradeTapeItem, TradeTapeLeg } from '../entities/drfq/trades';
import { DrfqLeg } from '../entities/drfq/domain';

interface LegItem {
  quantity: string | undefined;
  price?: string | null;
  isHedge: boolean;
}

/**
 * This function finds the base leg in a custom strategy, that is:
 * The leg that have the minimum quantity
 *
 * @param state - Current form state
 * @returns base leg
 */
export function findBaseLegIndex<T extends Draft<LegItem>>(legs: T[]): number {
  let minQty = Infinity;
  let minQtyIndex = -1;

  legs.forEach((leg, index) => {
    const { quantity } = leg;
    if (quantity == null || !isValidDecimal(quantity)) return;

    // Base leg can't be a hedge leg
    if (leg.isHedge) return;
    const numQty = parseFormattedFloat(quantity);

    if (numQty < minQty) {
      minQty = numQty;
      minQtyIndex = index;
    }
  });

  return minQtyIndex;
}

function getNonHedgeLegIndexForOptionsStrategies(
  legs: readonly TradeLeg[] | readonly TradeTapeLeg[] | readonly DrfqLeg[],
): number {
  return legs.findIndex(({ product_code }) => {
    const product = getProduct(product_code);
    return product.kind !== 'FUTURE';
  });
}

export function findDrfqBaseLegIndex(
  kind: TradeTapeItem['kind'],
  legs: readonly TradeLeg[] | readonly TradeTapeLeg[] | readonly DrfqLeg[],
): number {
  switch (kind) {
    case 'OPTION':
    case 'OPTION_FUTURE':
      return getNonHedgeLegIndexForOptionsStrategies(legs);
    case 'FUTURE':
      // future spreads pick first leg
      return 0;
    default:
      throw new TypeError('Not known Trade kind');
  }
}

export function findDrfqBaseLeg(
  kind: TradeTapeItem['kind'],
  legs: readonly TradeLeg[] | readonly TradeTapeLeg[] | readonly DrfqLeg[],
): TradeLeg | TradeTapeLeg | DrfqLeg | undefined {
  try {
    return legs[findDrfqBaseLegIndex(kind, legs)];
  } catch (error) {
    if (error instanceof TypeError) {
      console.error(error.message);
      return undefined;
    }
    throw error;
  }
}

/**
 * Distinct product codes across the STRATEGY (non-hedge) legs, in
 * first-appearance order. Deliberately not `rfq.product_codes`/
 * `trade.product_codes` — those backend fields are `[strategy_product_code,
 * hedge_product_code]` for ANY hedged RFQ (see `directed_rfq_v2/models.py`),
 * so an ordinary single-underlying option structure with a future hedge
 * already has 2 distinct codes. Checking "is this a multi-product package"
 * against the raw field would misclassify every hedged single-product RFQ
 * as multi-product. Use this wherever that distinction matters (a joined
 * label, or a `.length > 1` check) instead of the raw field.
 */
export function strategyProductCodes(
  legs: readonly TradeLeg[] | readonly TradeTapeLeg[] | readonly DrfqLeg[],
): readonly string[] {
  const codes: string[] = [];
  for (const leg of legs) {
    if (leg.isHedge) continue;
    if (!codes.includes(leg.product_code)) codes.push(leg.product_code);
  }
  return codes;
}

/**
 * Product code of the primary leg: the first non-hedge leg with
 * abs(ratio) === 1, or the first non-hedge leg if none has ratio 1.
 * Matches the backend's `rfq_package.primary_product_code` exactly — this
 * is what "the RFQ's product" now means once legs can span more than one
 * product (base_currency, settlement/max-size messages). Distinct from
 * findBaseProductCode/findDrfqBaseLegIndex, which pick a leg by INSTRUMENT
 * KIND (option over future) rather than by ratio; the two agree whenever
 * all legs share one product (today's only reachable case) and can diverge
 * once they don't.
 */
export function primaryProductCode(
  legs: readonly TradeLeg[] | readonly TradeTapeLeg[] | readonly DrfqLeg[],
): string | undefined {
  const [firstNonHedgeLeg, ...restNonHedgeLegs] = legs.filter(
    (leg) => !leg.isHedge,
  );
  if (firstNonHedgeLeg == null) return undefined;

  const ratioOneLeg = [firstNonHedgeLeg, ...restNonHedgeLegs].find((leg) =>
    BigNumber(leg.ratio).abs().isEqualTo(1),
  );

  return (ratioOneLeg ?? firstNonHedgeLeg).product_code;
}

export function roundedLegRatio(ratio: string) {
  const bnRatio = BigNumber(ratio);
  const fullPrecision = bnRatio.dp();

  if (fullPrecision == null || !bnRatio.isGreaterThan(0)) {
    return { roundedRatio: '', isRounded: false };
  }

  const isRounded = fullPrecision > VIEW_RATIO_PRECISION;
  const roundedValue = bnRatio.toFixed(
    VIEW_RATIO_PRECISION,
    BigNumber.ROUND_DOWN,
  );
  const postfix = isRounded ? '…' : '';

  const roundedRatio = `${roundedValue}${postfix}`;

  return { roundedRatio, isRounded };
}
